import { FC, useEffect, useState } from 'react';

import { router } from '@inertiajs/react';
import { ChevronDown, ChevronUp, Gamepad2, Save } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../components/ui/Card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/Select';
import { SelectedUniverseService } from '../../services/SelectedUniverseService';
import { Universe } from '../../types';

interface Props {
    isOpen: boolean;
    onToggle: () => void;
    universes: Universe[];
    currentUniverse?: Universe | null;
}

export const PreferredUniverseForm: FC<Props> = ({
    isOpen,
    onToggle,
    universes,
    currentUniverse,
}) => {
    const [selectedUniverseSlug, setSelectedUniverseSlug] = useState(
        currentUniverse?.slug ?? '',
    );
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        setSelectedUniverseSlug(currentUniverse?.slug ?? '');
    }, [currentUniverse?.slug]);

    const handleSave = () => {
        if (!selectedUniverseSlug) {
            return;
        }

        setProcessing(true);
        SelectedUniverseService.setPreference(selectedUniverseSlug);
        router.reload({ onFinish: () => setProcessing(false) });
    };

    return (
        <Card className="h-fit">
            <CardHeader
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={onToggle}
            >
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Gamepad2 className="h-5 w-5 text-primary" />
                        Основна гра
                    </div>
                    {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                </CardTitle>
                <CardDescription>
                    Оберіть гру, яка буде відкриватися як основна на сайті
                </CardDescription>
            </CardHeader>

            {isOpen && (
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Select
                            value={selectedUniverseSlug}
                            onValueChange={setSelectedUniverseSlug}
                            disabled={universes.length <= 1}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Оберіть гру" />
                            </SelectTrigger>
                            <SelectContent>
                                {universes.map((universe) => (
                                    <SelectItem
                                        key={universe.id}
                                        value={universe.slug}
                                    >
                                        {universe.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={
                            processing ||
                            universes.length <= 1 ||
                            selectedUniverseSlug === currentUniverse?.slug
                        }
                        className="w-full"
                    >
                        <Save className="mr-2 h-4 w-4" />
                        {processing ? 'Збереження...' : 'Зберегти основну гру'}
                    </Button>
                </CardContent>
            )}
        </Card>
    );
};
